// src/hooks/useEligibilityCheck.js
import { useCallback } from "react";
import { useSignMessage } from "wagmi";
import useWallet from "./useWallet";
import {
  requestEligibilitySignature,
  verifyEligibilitySignature,
} from "../api/eligibility";

const RETRYABLE_REASONS = new Set(["missing_signature", "nonce_missing", "nonce_expired", "bad_signature"]);

function classifyEligibilityResponse(resp) {
  const status = Number(resp?.status || 0);
  const body = resp?.body ?? null;
  const okHttp = Boolean(resp?.okHttp);

  if (!okHttp) {
    return {
      state: "error",
      status,
      body,
      reason: String(body?.error || body?.reason || `http_${status || "error"}`),
      message: "Verification failed, please retry",
    };
  }

  if (!body || body.ok !== true) {
    return {
      state: "error",
      status,
      body,
      reason: String(body?.error || body?.reason || "server_error"),
      message: "Verification failed, please retry",
    };
  }

  const eligible = body.eligible === true;
  const reason = String(body?.reason || body?.error || "");
  if (!eligible && reason === "not_allowlisted") {
    return {
      state: "ineligible",
      status,
      body,
      reason: "not_allowlisted",
      message: "This wallet is not eligible for presale access",
    };
  }
  if (!eligible) {
    return {
      state: "error",
      status,
      body,
      reason: String(reason || "server_error"),
      message: "Verification failed, please retry",
    };
  }

  return {
    state: "eligible",
    status,
    body,
    reason: String(reason || ""),
    message: "",
  };
}

export default function useEligibilityCheck() {
  const { isConnected, address } = useWallet();
  const { signMessageAsync } = useSignMessage();

  const checkEligibility = useCallback(async () => {
    if (!isConnected || !address) {
      throw new Error("Wallet not connected");
    }

    const connectedAddress = String(address || "").trim();

    const runAttempt = async (attemptNumber) => {
      const { signature } = await requestEligibilitySignature({
        address: connectedAddress,
        signMessageAsync,
      });

      const verifyResp = await verifyEligibilitySignature({ address: connectedAddress, signature });
      const classified = classifyEligibilityResponse(verifyResp);

      if (classified.state === "error" && attemptNumber === 0 && RETRYABLE_REASONS.has(String(classified.reason || ""))) {
        return runAttempt(1);
      }

      return classified;
    };

    return runAttempt(0);
  }, [isConnected, address, signMessageAsync]);

  return { checkEligibility };
}
